import { ModelConfig } from '@rematch/core';
import { localSDK as client } from '../../../sdk';
import {
    AuditLog,
    AuditLogFilters,
    AuditLogsResponse,
    EntityAuditResponse,
    AuditStatistics,
    AuditLogGroup,
    GroupedAuditLogsResponse
} from '../../../models/entities/audit/auditLog';

export type auditStateType = {
    // Main audit logs
    auditLogs: AuditLog[];
    currentFilters: AuditLogFilters;
    pagination: {
        totalCount: number;
        hasMore: boolean;
        limit: number;
        offset: number;
    };

    // Grouped audit logs
    groupedAuditLogs: AuditLogGroup[];
    groupedPagination: {
        totalGroups: number;
        hasMore: boolean;
        limit: number;
        offset: number;
    };

    // Search results
    searchResults: AuditLog[];
    searchQuery: string;

    // Entity-specific audit
    entityAudit: {
        entityType: string;
        entityId: string;
        entityName?: string;
        totalChanges: number;
        firstChange: string;
        lastChange: string;
        uniqueUsers: number;
        changes: AuditLog[];
    } | null;

    // Statistics
    statistics: AuditStatistics | null;

    // Metadata
    entityTypes: string[];
    auditUsers: Array<{ userId: string; userName: string }>;

    // UI state
    loading: boolean;
    error: string | null;
};

export const audit: ModelConfig<auditStateType> = {
    state: {
        auditLogs: [],
        currentFilters: {},
        pagination: {
            totalCount: 0,
            hasMore: false,
            limit: 50,
            offset: 0
        },
        groupedAuditLogs: [],
        groupedPagination: {
            totalGroups: 0,
            hasMore: false,
            limit: 50,
            offset: 0
        },
        searchResults: [],
        searchQuery: '',
        entityAudit: null,
        statistics: null,
        entityTypes: [],
        auditUsers: [],
        loading: false,
        error: null
    },
    reducers: {
        setAuditLogs(state: auditStateType, { data, pagination }: AuditLogsResponse): auditStateType {
            // Ensure no duplicates by filtering based on id
            const uniqueLogs = data.filter((newLog, index, self) => 
                index === self.findIndex(log => log.id === newLog.id)
            );
            
            console.log('🔄 setAuditLogs reducer called:', {
                incoming: data.length,
                unique: uniqueLogs.length,
                pagination
            });
            
            return { 
                ...state, 
                auditLogs: uniqueLogs,
                pagination,
                error: null
            };
        },
        
        appendAuditLogs(state: auditStateType, { data, pagination }: AuditLogsResponse): auditStateType {
            // Merge and deduplicate logs
            const existingIds = new Set(state.auditLogs.map(log => log.id));
            const newLogs = data.filter(log => !existingIds.has(log.id));
            
            return {
                ...state,
                auditLogs: [...state.auditLogs, ...newLogs],
                pagination,
                error: null
            };
        },
        
        setCurrentFilters(state: auditStateType, filters: AuditLogFilters): auditStateType {
            return { ...state, currentFilters: filters };
        },
        
        setSearchResults(state: auditStateType, { data }: AuditLogsResponse): auditStateType {
            return { 
                ...state, 
                searchResults: data,
                error: null
            };
        },
        
        setSearchQuery(state: auditStateType, query: string): auditStateType {
            return { ...state, searchQuery: query };
        },
        
        setEntityAudit(state: auditStateType, entityAudit: EntityAuditResponse['data'] | null): auditStateType {
            return { ...state, entityAudit, error: null };
        },
        
        setStatistics(state: auditStateType, statistics: AuditStatistics | null): auditStateType {
            return { ...state, statistics, error: null };
        },
        
        setEntityTypes(state: auditStateType, entityTypes: string[]): auditStateType {
            return { ...state, entityTypes };
        },
        
        setAuditUsers(state: auditStateType, auditUsers: Array<{ userId: string; userName: string }>): auditStateType {
            return { ...state, auditUsers };
        },
        
        setLoading(state: auditStateType, loading: boolean): auditStateType {
            return { ...state, loading };
        },
        
        setError(state: auditStateType, error: string | null): auditStateType {
            return { ...state, error };
        },
        
        clearSearchResults(state: auditStateType): auditStateType {
            return { ...state, searchResults: [], searchQuery: '' };
        },
        
        clearEntityAudit(state: auditStateType): auditStateType {
            return { ...state, entityAudit: null };
        },
        
        clearStatistics(state: auditStateType): auditStateType {
            return { ...state, statistics: null };
        },

        // Grouped audit logs reducers
        setGroupedAuditLogs(state: auditStateType, { data, pagination }: GroupedAuditLogsResponse): auditStateType {
            const uniqueGroups = data.filter((newGroup, index, self) =>
                index === self.findIndex(group => group.sessionId === newGroup.sessionId)
            );

            console.log('🔄 setGroupedAuditLogs reducer called:', {
                incoming: data.length,
                unique: uniqueGroups.length,
                pagination
            });

            return {
                ...state,
                groupedAuditLogs: uniqueGroups,
                groupedPagination: pagination,
                error: null
            };
        },

        appendGroupedAuditLogs(state: auditStateType, { data, pagination }: GroupedAuditLogsResponse): auditStateType {
            const existingIds = new Set(state.groupedAuditLogs.map(group => group.sessionId));
            const newGroups = data.filter(group => !existingIds.has(group.sessionId));

            return {
                ...state,
                groupedAuditLogs: [...state.groupedAuditLogs, ...newGroups],
                groupedPagination: pagination,
                error: null
            };
        },

    },
    effects: (dispatch: any) => ({
        async fetchAuditLogs(filters: AuditLogFilters = {}, loadMore: boolean = false, rootState?: any) {
            try {
                const usePlatformAudit = rootState?.platformSuperAdmin?.usePlatformAudit === true;
                const isLoadMore = typeof loadMore === 'boolean' && loadMore === true;

                dispatch.audit.setLoading(true);

                const finalFilters = isLoadMore
                    ? { ...filters }
                    : { ...filters, offset: filters.offset !== undefined ? filters.offset : 0 };

                if (!isLoadMore) {
                    dispatch.audit.setSearchQuery('');
                }

                dispatch.audit.setCurrentFilters(finalFilters);
                const response = await client.audit().fetchAuditLogs(finalFilters, usePlatformAudit);

                if (response.success) {
                    if (isLoadMore) {
                        dispatch.audit.appendAuditLogs(response);
                    } else {
                        dispatch.audit.setAuditLogs(response);
                    }
                } else {
                    dispatch.audit.setError('Failed to fetch audit logs');
                }
            } catch (error: any) {
                dispatch.audit.setError(error.message || 'Failed to fetch audit logs');
            } finally {
                dispatch.audit.setLoading(false);
            }
        },

        async searchAuditLogs({ query, filters = {} }: { query: string; filters?: AuditLogFilters }, rootState?: any) {
            try {
                const usePlatformAudit = rootState?.platformSuperAdmin?.usePlatformAudit === true;
                dispatch.audit.setLoading(true);
                dispatch.audit.setSearchQuery(query);

                const response = await client.audit().searchAuditLogs(query, filters, usePlatformAudit);
                
                if (response.success) {
                    dispatch.audit.setSearchResults(response);
                } else {
                    dispatch.audit.setError('Failed to search audit logs');
                }
            } catch (error: any) {
                dispatch.audit.setError(error.message || 'Failed to search audit logs');
            } finally {
                dispatch.audit.setLoading(false);
            }
        },
        
        async fetchEntityAudit({ entityType, entityId, limit = 100 }: { entityType: string; entityId: string; limit?: number }) {
            try {
                dispatch.audit.setLoading(true);
                
                const response = await client.audit().fetchEntityAudit(entityType, entityId, limit);
                
                if (response.success) {
                    dispatch.audit.setEntityAudit(response.data);
                } else {
                    dispatch.audit.setError('Failed to fetch entity audit');
                }
            } catch (error: any) {
                dispatch.audit.setError(error.message || 'Failed to fetch entity audit');
            } finally {
                dispatch.audit.setLoading(false);
            }
        },
        
        async fetchStatistics(filters: Omit<AuditLogFilters, 'limit' | 'offset'> = {}) {
            try {
                dispatch.audit.setLoading(true);
                
                const response = await client.audit().fetchAuditStatistics(filters);
                
                if (response.success) {
                    dispatch.audit.setStatistics(response.data);
                } else {
                    dispatch.audit.setError('Failed to fetch audit statistics');
                }
            } catch (error: any) {
                dispatch.audit.setError(error.message || 'Failed to fetch audit statistics');
            } finally {
                dispatch.audit.setLoading(false);
            }
        },
        
        async exportAuditData({ filters = {}, format = 'csv' }: { filters?: AuditLogFilters; format?: 'csv' | 'excel' }) {
            try {
                dispatch.audit.setLoading(true);
                
                const blob = await client.audit().exportAuditData(filters, format);
                
                // Create download link
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `audit-export-${new Date().toISOString().split('T')[0]}.${format}`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
                
            } catch (error: any) {
                dispatch.audit.setError(error.message || 'Failed to export audit data');
            } finally {
                dispatch.audit.setLoading(false);
            }
        },
        
        async fetchMetadata(_payload: void, rootState: any) {
            try {
                const usePlatformAudit = rootState.platformSuperAdmin?.usePlatformAudit === true;
                const companyId = rootState.platformSuperAdmin?.activeTenantId || undefined;
                const [entityTypesResponse, usersResponse] = await Promise.all([
                    client.audit().fetchEntityTypes(usePlatformAudit),
                    client.audit().fetchAuditUsers(companyId, usePlatformAudit)
                ]);

                if (entityTypesResponse.success) {
                    dispatch.audit.setEntityTypes(entityTypesResponse.data);
                }

                if (usersResponse.success) {
                    dispatch.audit.setAuditUsers(usersResponse.data);
                }
            } catch (error: any) {
                dispatch.audit.setError(error.message || 'Failed to fetch metadata');
            }
        },

        async fetchGroupedAuditLogs(filters: AuditLogFilters = {}, loadMore: boolean = false, rootState?: any) {
            try {
                const usePlatformAudit = rootState?.platformSuperAdmin?.usePlatformAudit === true;
                const isLoadMore = typeof loadMore === 'boolean' && loadMore === true;

                dispatch.audit.setLoading(true);

                const finalFilters = isLoadMore
                    ? { ...filters }
                    : { ...filters, offset: filters.offset !== undefined ? filters.offset : 0 };

                if (!isLoadMore) {
                    dispatch.audit.setSearchQuery('');
                }

                dispatch.audit.setCurrentFilters(finalFilters);
                const response = await client.audit().fetchGroupedAuditLogs(finalFilters, usePlatformAudit);

                if (response.success) {
                    if (isLoadMore) {
                        dispatch.audit.appendGroupedAuditLogs(response);
                    } else {
                        dispatch.audit.setGroupedAuditLogs(response);
                    }
                } else {
                    dispatch.audit.setError('Failed to fetch grouped audit logs');
                }
            } catch (error: any) {
                dispatch.audit.setError(error.message || 'Failed to fetch grouped audit logs');
            } finally {
                dispatch.audit.setLoading(false);
            }
        }
    })
};